
    // IMPORTS
    import { computed, defineComponent, watchEffect } from 'vue'
    import router from '@/router';

    import { useEquipmentStore } from '@/store/equipment';
    import { buildLocalization } from '@/util'

    import { StackLayout, Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import DocLink from '@/components/common/doc-link.vue'

    import { DocumentNames, DocumentVisibility, IDocumentDTO, IDocumentDisplayInfo } from '@/types/document';

    export default defineComponent({
        name: 'equipmentDocuments',

        components: {
            LoaderGuard,
            DocLink,
            StackLayout,
            Card,
            CardHeader,
            CardTitle,
            CardBody
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const equipmentStore = useEquipmentStore()
            const docSortOrder = [
                'UMA',
                'EKI',
                'EKO',
                'ELB',
                'ELD',
                'PID',
                'TDA',
                'SPD',
                'INS'
            ]

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const publicData = { }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const documents = computed(() => equipmentStore.documents)
            const hasLoaded = computed(() => documents.value != null)
            const userManual = computed(() => documents.value?.filter(doc => doc.documentType === 'SND' && doc.visibility != DocumentVisibility.AdminsOnly ))
            const otherDocs = computed(() => documents.value?.filter(doc => doc.documentType != 'SND' && doc.visibility != DocumentVisibility.AdminsOnly))
            const adminDocs = computed(() => documents.value?.filter(doc => doc.visibility === DocumentVisibility.AdminsOnly))
            const userManualNames = computed(() => getDocNames(userManual.value))
            const otherDocsNames = computed(() => getDocNames(otherDocs.value))
            const adminDocsNames = computed(() => getDocNames(adminDocs.value))
            const versionLabel = computed(() => localize('version'))

            const computedData = {
                documents,
                hasLoaded,
                userManual,
                otherDocs,
                adminDocs,
                userManualNames,
                otherDocsNames,
                adminDocsNames,
                versionLabel
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization();

            const publicMethods = { localize }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function getDocNames(documents: IDocumentDTO[] | undefined): IDocumentDisplayInfo[] {
                const docNames = documents
                    ? new DocumentNames(documents).localizedNamesOfDocuments()
                    : [] 

                return specificOrder(docNames)
            }

            function specificOrder(docNames: IDocumentDisplayInfo[]): IDocumentDisplayInfo[] {
                return docNames.sort((a,b) => {
                    let indexA = docSortOrder.indexOf(a.document.documentType)
                    let indexB = docSortOrder.indexOf(b.document.documentType)

                    if (indexA < 0) indexA = docNames.length // put last
                    if (indexB < 0) indexB = docNames.length // put last

                    const result = indexA - indexB

                    return result == 0
                        ? a.localizedDocName.localeCompare(b.localizedDocName)
                        : result
                })
            }

            /********************************************************************************************************************
             WATCHERS
             ********************************************************************************************************************/
            // Redirect, if the document tab is supposed to be hidden
            watchEffect(() => {
                if (equipmentStore.showDocumentTab === false) {
                    router.replace({name: 'equipment-spareparts'})
                }
            })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
